import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Smartphone, Loader2, Copy, Lock, CheckCircle, XCircle, History, LogOut } from "lucide-react";
import { toast } from "sonner";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472", LINE = "#E7E1D6", GREEN = "#1E7E45";
const codeInput = "fundio-input text-center tracking-widest";

function copy(text) { navigator.clipboard?.writeText(text); toast.success("Kopiert!"); }

function TwoFA() {
  const { user, refreshUser } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const startSetup = async () => { setLoading(true); try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); } finally { setLoading(false); } };
  const enable = async () => { setLoading(true); try { await api.post("/auth/2fa/enable", { code }); toast.success("2FA aktiviert!"); setSetup(null); setCode(""); await refreshUser(); } catch (e) { toast.error(e.response?.data?.detail || "Code ungültig"); } finally { setLoading(false); } };
  const disable = async () => { setLoading(true); try { await api.post("/auth/2fa/disable", { code }); toast.success("2FA deaktiviert."); setCode(""); await refreshUser(); } catch (e) { toast.error(e.response?.data?.detail || "Code ungültig"); } finally { setLoading(false); } };

  return (
    <div className="fundio-panel" data-testid="twofa-section">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-[17px] font-bold" style={{ color: INK }}><Smartphone size={17} style={{ color: GOLD }} /> Zwei-Faktor-Authentifizierung</h2>
        {user?.two_factor_enabled
          ? <span className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold" style={{ background: "rgba(30,126,69,.12)", color: GREEN }}><CheckCircle size={13} /> Aktiv</span>
          : <span className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold" style={{ background: "#F1EEE7", color: SLATE }}><XCircle size={13} /> Inaktiv</span>}
      </div>
      {user?.two_factor_enabled ? (
        <div className="mt-4">
          <p className="text-sm" style={{ color: MUTED }}>Dein Konto ist mit einer Authenticator-App geschützt. Zum Deaktivieren aktuellen Code eingeben.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <input data-testid="twofa-disable-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-stelliger Code" className={codeInput} style={{ width: 180 }} />
            <button data-testid="twofa-disable-btn" onClick={disable} disabled={loading || code.length < 6} className="fundio-action" style={{ color: "#903824", borderColor: "#E7B9AF" }}>
              {loading && <Loader2 size={15} className="animate-spin" />} 2FA deaktivieren
            </button>
          </div>
        </div>
      ) : !setup ? (
        <div className="mt-4">
          <p className="text-sm" style={{ color: MUTED }}>Schütze dein Konto zusätzlich mit einer Authenticator-App (Google Authenticator, Authy, Microsoft Authenticator).</p>
          <button data-testid="twofa-setup-btn" onClick={startSetup} disabled={loading} className="fundio-action fundio-action--gold mt-3">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />} 2FA einrichten
          </button>
        </div>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-[auto,1fr]">
          <div className="rounded-lg border p-2" style={{ borderColor: LINE }}><img src={setup.qr} alt="QR-Code" className="h-40 w-40" data-testid="twofa-qr" /></div>
          <div>
            <p className="text-sm" style={{ color: MUTED }}>1. Scanne den QR-Code mit deiner Authenticator-App.</p>
            <p className="mt-1 text-xs" style={{ color: SLATE }}>Oder Schlüssel manuell eingeben:</p>
            <button onClick={() => copy(setup.secret)} className="mt-1 flex items-center gap-2 rounded bg-fundio-subtle px-3 py-1.5 font-mono text-xs" style={{ color: GOLD }}><Copy size={12} /> {setup.secret}</button>
            <p className="mt-3 text-sm" style={{ color: MUTED }}>2. Gib den angezeigten 6-stelligen Code ein:</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <input data-testid="twofa-enable-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" className={codeInput} style={{ width: 128 }} />
              <button data-testid="twofa-enable-btn" onClick={enable} disabled={loading || code.length < 6} className="fundio-action" style={{ background: GREEN, borderColor: GREEN, color: "#fff" }}>
                {loading && <Loader2 size={15} className="animate-spin" />} Aktivieren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Sicherheit() {
  const { logoutAll } = useAuth();
  const [role, setRole] = useState(null);
  const [require2fa, setRequire2fa] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = role === "owner" || role === "admin";

  useEffect(() => {
    api.get("/org/current").then(({ data }) => { if (data.status === "ok") setRole(data.tenant.role); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (isAdmin) api.get("/auth/security").then(({ data }) => setRequire2fa(data.require_2fa)).catch(() => {});
  }, [isAdmin]);

  const toggle = async () => {
    setSaving(true);
    try { const { data } = await api.put("/auth/security", { require_2fa: !require2fa }); setRequire2fa(data.require_2fa); toast.success("Gespeichert."); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  return (
    <CockpitLayout active="sicherheit" title="Sicherheit">
      <div className="fundio-page-stack" data-testid="gold-sicherheit">
        <div className="fundio-section-head">
          <div>
            <h1 className="fundio-page-title flex items-center gap-2.5" data-testid="sicherheit-title">
              <ShieldCheck size={28} style={{ color: GOLD }} /> Sicherheit
            </h1>
            <p className="mt-1 fundio-muted text-[14px]">Konto-Sicherheit und Zwei-Faktor-Authentifizierung.</p>
          </div>
        </div>

        <TwoFA />

        <div className="fundio-panel" data-testid="security-section">
          <h2 className="flex items-center gap-2 text-[17px] font-bold" style={{ color: INK }}><ShieldCheck size={17} style={{ color: GOLD }} /> Zugang & Sitzungen</h2>
          {isAdmin && (
            <div className="mt-4 flex items-center justify-between gap-3 pb-4" style={{ borderBottom: `1px solid ${LINE}` }}>
              <div>
                <p className="text-sm font-semibold" style={{ color: INK }}>2FA für alle Team-Mitglieder verpflichtend</p>
                <p className="text-xs" style={{ color: SLATE }}>Gilt für diesen Brocki. Jede Person richtet beim Login eine Authenticator-App ein.</p>
              </div>
              <button data-testid="require-2fa-toggle" onClick={toggle} disabled={saving || require2fa === null}
                className="relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-50" style={{ background: require2fa ? GREEN : "#D8D3C8" }}>
                <span className="absolute top-1 h-5 w-5 rounded-full bg-white transition-all" style={{ left: require2fa ? "1.5rem" : "0.25rem" }} />
              </button>
            </div>
          )}
          <div className="mt-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold" style={{ color: INK }}>Auf allen Geräten abmelden</p>
              <p className="text-xs" style={{ color: SLATE }}>Beendet alle aktiven Sitzungen (auch auf anderen Geräten).</p>
            </div>
            <button data-testid="logout-all-btn" onClick={logoutAll} className="fundio-action shrink-0"><LogOut size={15} /> Abmelden</button>
          </div>
        </div>

        <Link to="/dashboard/aktivitaet" data-testid="activity-link" className="fundio-panel flex items-center justify-between transition-colors hover:border-fundio-gold">
          <span className="flex items-center gap-2 text-[17px] font-bold" style={{ color: INK }}><History size={17} style={{ color: GOLD }} /> Aktivitäts-Protokoll</span>
          <span className="text-sm" style={{ color: SLATE }}>ansehen →</span>
        </Link>
      </div>
    </CockpitLayout>
  );
}
